// online (catalog-outline.md "online"; ops parity api-commerce/online-ordering.md §1.1-§1.6, §2.2, §2.18; backoffice
// pages/online-store.md): a location's online store as multi-origin config (ADR-015). The backoffice edits it from the
// cloud; the register pauses and resumes it and picks the default prep time at the node. Whether the store takes
// orders right now is computed by core/online from these fields, the hours and the location's open tills.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { defineError } from '../../define/errors.ts';
import { secret } from '../../define/secret.ts';
import { DiningOptionId, EmployeeId, EpochMs, IsoInstant, LocationId, OnlineStoreId, PrinterId } from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten, ProposalRequired } from '../config/index.ts';
import { qrCodeCommands, qrCodeQueries } from './qr.ts';
import { qrTemplateCommands, qrTemplateQueries } from './qr-templates.ts';

/** HH:mm:ss on the location's wall clock (legacy TIME columns). */
const WallTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/);

/** One weekday's hours (legacy `online_store_menu_hours` / `business_hours`): up to two windows, or closed. */
export const DayHours = z
  .object({
    /** ISO weekday, 1 = Monday. */
    weekday: z.int().min(1).max(7),
    closed: z.boolean(),
    start1: WallTime.nullable(),
    end1: WallTime.nullable(),
    start2: WallTime.nullable(),
    end2: WallTime.nullable(),
  })
  .meta({ id: 'DayHours' });

/** Legacy `online_store_special_hour_closures`: the store is closed between two instants. */
export const SpecialClosure = z
  .object({ closureId: z.uuid({ version: 'v7' }), startsAt: EpochMs, endsAt: EpochMs, reason: z.string().trim().min(1).max(255) })
  .meta({ id: 'SpecialClosure' });

/** Legacy `online_store_prep_times`: how long an order takes; the default one sets new orders' estimate. */
export const PrepTime = z
  .object({
    prepTimeId: z.uuid({ version: 'v7' }),
    name: z.string().trim().min(1).max(255),
    minutes: z.int().min(1).max(1440),
    isDefault: z.boolean(),
    color: z.string().max(32),
  })
  .meta({ id: 'PrepTime' });

/** Legacy `online_store_banners`: the message at the top of the online menu; one is active. */
export const StoreBanner = z
  .object({
    bannerId: z.uuid({ version: 'v7' }),
    message: z.string().trim().min(1).max(150),
    additionalDetails: z.string().max(480),
    active: z.boolean(),
    themeColor: z.enum(['teal', 'purple', 'orange', 'green']),
    icon: z.enum(['message', 'price_tag', 'megaphone', 'alert']).nullable(),
  })
  .meta({ id: 'StoreBanner' });

/** Legacy `online_order_notifications`: the customer notices, each on or off with its message. */
export const StoreNotices = z
  .object({
    accept: z.object({ enabled: z.boolean(), message: z.string().max(350) }),
    reject: z.object({ enabled: z.boolean(), message: z.string().max(350) }),
    delay: z.object({ enabled: z.boolean(), message: z.string().max(350) }),
  })
  .meta({ id: 'StoreNotices' });

/**
 * Legacy `voice_ordering_*` and `voice_*` store columns (Voice Ordering tab). Core's validateVoiceSettings holds the
 * rules (voice allowlist, session and budget bounds, the instructions' character and byte caps).
 */
export const VoiceSettings = z
  .object({
    enabled: z.boolean(),
    /** Null: the platform default voice. */
    voiceName: z.string().max(32).nullable(),
    sessionMaxSeconds: z.int().min(60).max(600),
    dailySessionBudget: z.int().min(0).max(10000),
    instructions: z.string().max(2000).nullable(),
    shareDietaryInfo: z.boolean(),
  })
  .meta({ id: 'VoiceSettings' });

/** Legacy `order_approval`: accept new orders by hand, or send them straight to the kitchen. */
export const OrderApproval = z.enum(['manually_approve', 'send_to_kitchen']).meta({ id: 'OrderApproval' });

export const OnlineStoreFields = z
  .object({
    /**
     * Store Info's name, email and phone: empty on a store created from another tab (legacy's auto-created store, L185
     * reversed); core requires them on any save that changes Store Info.
     */
    displayName: z.string().trim().max(255),
    businessDescription: z.string().max(2000),
    address: z.string().max(225),
    city: z.string().max(255),
    state: z.string().max(255),
    zipcode: z.string().max(10),
    contactFirstName: z.string().max(255),
    contactLastName: z.string().max(255),
    email: z.union([z.email().max(255), z.literal('')]),
    phone: z.string().trim().max(32),
    /** The storefront's host prefix, stored slugified (legacy `Str::slug`), unique across stores. Admins and resellers set it. */
    subdomain: z.string().trim().min(3).max(63).nullable(),
    /** Legacy `status`: the master switch. Off = paused, whatever the hours say. */
    acceptingOrders: z.boolean(),
    /** Snooze: no orders until `snoozedUntil` (legacy never enforced it; Q-OS4 fixed, B300). */
    snoozed: z.boolean(),
    snoozedUntil: EpochMs.nullable(),
    snoozedMinutes: z.int().min(0).max(1440),
    /** Menu visible, ordering off. */
    catalogMode: z.boolean(),
    orderApproval: OrderApproval.nullable(),
    /** Printers online orders print at (legacy `online_store_printer`). */
    printerIds: z.array(PrinterId).max(50),
    menuHours: z.array(DayHours).max(7),
    /** Stored and shown; nothing reads them (Q-OS5, kept). */
    businessHours: z.array(DayHours).max(7),
    specialClosures: z.array(SpecialClosure).max(200),
    prepTimes: z.array(PrepTime).max(50),
    banners: z.array(StoreBanner).max(50),
    /**
     * Absent until the Notifications tab is first saved (legacy has no `online_order_notifications` row yet): the tab
     * reads that as all three on with empty messages (core `storeNotices`). Nothing mails from these (Q-OS11).
     */
    notices: StoreNotices.optional(),
    /** Admin and reseller only (API Keys tab): the online payment gateway and merchant. Keys never live here. */
    gateway: z.enum(['sandbox', 'datacap', 'clearent', 'payrix', 'fluidpay']).nullable(),
    merchantId: z.string().max(255).nullable(),
    /**
     * The gateway's public (tokenization) key the storefront hands every shopper (Clearent "Public API key", Datacap
     * "Pronto API (public key)", Q-OS-04). Admin and reseller only. Absent on stores saved before it existed.
     */
    gatewayPublicKey: z.string().max(2000).nullable().optional(),
    /**
     * The last 4 characters of the gateway's private key and when it was set (A34). The key itself is sealed in the
     * cloud beside the store and never read back; only `online.setGatewayPrivateKey` writes these. Absent until set.
     */
    gatewayPrivateKeyLast4: z.string().max(4).nullable().optional(),
    gatewayPrivateKeySetAt: IsoInstant.nullable().optional(),
    /** Voice Ordering tab. Absent on stores saved before it existed: read as core's DEFAULT_VOICE_SETTINGS. */
    voice: VoiceSettings.optional(),
  })
  .meta({ id: 'OnlineStoreFields' });

export const OnlineStoreView = OnlineStoreFields.extend({ onlineStoreId: OnlineStoreId, locationId: LocationId, version: z.int().nonnegative() })
  .meta({ id: 'OnlineStoreView' });

/** What the storefront and the register show: open now and until when, or closed and when it opens next. */
export const OnlineStoreStatus = z
  .object({ status: z.enum(['online', 'offline']), openUntil: EpochMs.nullable(), opensNext: EpochMs.nullable() })
  .meta({ id: 'OnlineStoreStatus' });

// ---- errors ----------------------------------------------------------------------------------------------------------
export const OnlineStoreInvalid = defineError({
  code: 'online.invalid', httpStatus: 422, message: 'errors.online.invalid', retryable: false,
  details: z.object({ rule: z.string(), field: z.string().optional() }),
});
export const OnlineStoreNotFound = defineError({
  code: 'online.store_not_found', httpStatus: 404, message: 'errors.online.store_not_found', retryable: false,
  details: z.object({ locationId: LocationId }),
});

/** A save that changes an API Keys field (gateway, subdomain, merchant, public key) from a session that is not Radish staff or a reseller. */
export const OnlineGatewayRestricted = defineError({
  code: 'online.gateway_restricted', httpStatus: 403, message: 'errors.online.gateway_restricted', retryable: false,
  details: z.object({ field: z.string() }),
});

const configErrors = [OnlineStoreInvalid, OnlineGatewayRestricted, CloudAuthoritative, ProposalRequired, OnlineStoreNotFound] as const;
const common = { availability: 'location', origin: 'any', emits: [ConfigFieldsWritten], errors: configErrors, dryRun: true } as const;

// ---- commands --------------------------------------------------------------------------------------------------------
/** What a save may change: every field but the private key's trace, which only `online.setGatewayPrivateKey` writes. */
const StoreChanges = OnlineStoreFields.omit({ gatewayPrivateKeyLast4: true, gatewayPrivateKeySetAt: true }).partial();

/** A gateway private key (Clearent API key, Datacap Pay API password): a credential, never stored in the clear (D7). */
const GatewayPrivateKey = secret(z.string().trim().min(1).max(2000));

export const UpsertOnlineStore = defineCommand({
  ...common,
  name: 'online.upsertStore',
  summary: "Create the location's online store or change it: store info, ordering mode, hours, closures, prep times, banners, notices.",
  input: z.object({ onlineStoreId: OnlineStoreId, locationId: LocationId, changes: StoreChanges }),
  output: OnlineStoreView,
  permission: 'online.manage',
  risk: 'medium',
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: [
    'POST /v1/online-store', 'PUT /v1/online-store/{id}', 'PATCH /v1/online-store/{id}/subdomain', 'POST /v1/online-store/hours',
    'POST /v1/online-store/special-closure', 'POST /v1/online-store/prep-times', 'POST /v1/online-store/banners',
    'POST /v1/online-store/notifications', 'PUT /v1/online-store/voice-ordering', 'PATCH /v1/online-store/{id}/locations/{location}/gateway', 'PATCH /v1/online-store/{id}/locations/{location}/merchant',
  ],
});

/**
 * What a page outside the Online Store reads of it, with no Manage Online Store: whether the location has a store
 * (legacy's location resource `online_store_enabled`, which shows the lists' Online Orders toggles) and its business
 * hours (Business Profile › Business Hours, gated on Manage Location Settings). Nothing about payments.
 */
/**
 * What the register's two store commands answer: which store, at which version. A PIN session gets nothing more, never
 * the gateway fields (legacy's register status-change returned the private key itself: B-row in bugs.md).
 */
export const OnlineStoreVersion = z.object({ onlineStoreId: OnlineStoreId, version: z.int().nonnegative() }).meta({ id: 'OnlineStoreVersion' });

export const OnlineStoreSummary = z
  .object({ onlineStoreId: OnlineStoreId, version: z.int().nonnegative(), businessHours: z.array(DayHours).max(7) })
  .meta({ id: 'OnlineStoreSummary' });

/**
 * Business Profile's Business Hours tab (legacy `POST /v1/online-store/business-hours`): the week's business hours, kept
 * on the online store as legacy keeps them. Gated as the tab is (legacy page meta `manage_location_settings`), not as
 * the Online Store pages; a location without an online store is refused with `online.store_not_found`.
 */
export const SetBusinessHours = defineCommand({
  ...common,
  name: 'online.setBusinessHours',
  summary: "Set the location's weekly business hours (Business Profile).",
  input: z.object({ onlineStoreId: OnlineStoreId, locationId: LocationId, businessHours: z.array(DayHours).max(7) }),
  // The summary, not the whole store: this tab's users need not hold Manage Online Store (online.store.summary).
  output: OnlineStoreSummary,
  permission: 'settings.location.manage',
  risk: 'low',
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['POST /v1/online-store/business-hours'],
});

/** The register's switch (legacy `POST /v1/online-store/status-change`). */
export const SetAcceptingOrders = defineCommand({
  ...common,
  name: 'online.setAcceptingOrders',
  summary: 'Pause or resume online ordering from the register.',
  // PRM-012, B684: the switch needs manage_online_store_settings; `approvedBy` names the manager whose PIN approved it for
  // someone without it, and every receiver of the register's fact checks the grant (core storeWriteRefusal).
  input: z.object({ onlineStoreId: OnlineStoreId, locationId: LocationId, acceptingOrders: z.boolean(), approvedBy: EmployeeId.optional() }),
  output: OnlineStoreVersion,
  permission: 'online.storeStatus',
  risk: 'low',
  agent: { expose: false },
  legacy: ['POST /v1/online-store/status-change'],
});

/** The register's prep time picker (legacy `PUT /online-store/prep-times/{id}/default`, with Q-OS24 fixed: this store only). */
export const SetDefaultPrepTime = defineCommand({
  ...common,
  name: 'online.setDefaultPrepTime',
  summary: "Choose the prep time new online orders are estimated with.",
  input: z.object({ onlineStoreId: OnlineStoreId, locationId: LocationId, prepTimeId: z.uuid({ version: 'v7' }) }),
  output: OnlineStoreVersion,
  permission: { session: 'staff' },
  risk: 'low',
  agent: { expose: false },
  legacy: ['PUT /v1/online-store/prep-times/{id}/default'],
});

/**
 * The API Keys tab's private key (A34): sealed in the cloud beside the store with the same sealing as a device's
 * Terminal ID, write-only (the store shows its last 4), and read only by the cloud's card charge. Null removes it.
 */
export const SetGatewayPrivateKey = defineCommand({
  name: 'online.setGatewayPrivateKey',
  summary: "Set or remove the online store's payment gateway private key. Staff and resellers only; it is never shown again.",
  input: z.object({ onlineStoreId: OnlineStoreId, locationId: LocationId, privateKey: GatewayPrivateKey.nullable() }),
  output: z.object({ onlineStoreId: OnlineStoreId, privateKeyLast4: z.string().max(4).nullable(), setAt: IsoInstant }),
  permission: 'settings.platform.manage',
  risk: 'high',
  availability: 'cloud',
  origin: 'cloud',
  cloudReason: 'The key is sealed with a cloud-held key and only the cloud charges online cards; it never reaches a node or register.',
  emits: [ConfigFieldsWritten],
  errors: [OnlineStoreNotFound],
  dryRun: true,
  agent: { expose: false },
  legacy: ['PATCH /v1/online-store/{id}/locations/{location}/private-key'],
});

// ---- queries ---------------------------------------------------------------------------------------------------------
export const GetOnlineStore = defineQuery({
  name: 'online.store.get',
  summary: "The location's online store, or null when it has none.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ store: OnlineStoreView.nullable() }),
  // Legacy's back office online-store routes all sit behind manage_online_store; this read answered any user session
  // with the gateway, merchant ID, public key and the private key's last 4 (coordinator 2026-10-18, review of #1387).
  permission: 'online.manage',
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/online-store'],
});

export const GetOnlineStoreSummary = defineQuery({
  name: 'online.store.summary',
  summary: "Whether the location has an online store, and the store's business hours.",
  input: z.object({ locationId: LocationId }),
  output: z.object({ store: OnlineStoreSummary.nullable() }),
  permission: { session: 'user' },
  availability: 'location',
  scope: 'location',
  errors: [],
  agent: { expose: false },
  legacy: ['GET /v1/online-store/business-hours'],
});

export const GetOnlineStoreStatus = defineQuery({
  name: 'online.store.status',
  summary: 'Whether the online store takes orders now, until when, or when it opens next.',
  input: z.object({ locationId: LocationId }),
  output: OnlineStoreStatus,
  permission: { session: 'staff' },
  availability: 'location',
  scope: 'location',
  errors: [OnlineStoreNotFound],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/online-store/{id}/status'],
});

// ---- voice conversations (cloud) -------------------------------------------------------------------------------------
/** Legacy `voice_sessions.session_outcome`; a newer worker may write others, which the page shows as written. */
export const VoiceOutcome = z.string().max(64).meta({ id: 'VoiceOutcome' });

/** One voice ordering session as the Conversations list shows it (legacy VoiceConversation). */
export const VoiceConversationSummary = z
  .object({
    conversationId: z.uuid({ version: 'v7' }),
    startedAt: IsoInstant.nullable(),
    endedAt: IsoInstant.nullable(),
    durationSeconds: z.int().nonnegative().nullable(),
    outcome: VoiceOutcome.nullable(),
    /** Saved turns. */
    turnCount: z.int().nonnegative(),
    /** What the worker said it had; more than `turnCount` means ingest dropped some. */
    reportedTurnCount: z.int().nonnegative().nullable(),
    finalCartItemCount: z.int().nonnegative().nullable(),
    unresolvedPhrases: z.array(z.string().max(500)).max(100),
  })
  .meta({ id: 'VoiceConversationSummary' });

export const VoiceTurn = z
  .object({
    turnId: z.uuid({ version: 'v7' }),
    turnIndex: z.int().nonnegative(),
    speaker: z.enum(['customer', 'agent']),
    content: z.string(),
    /** Worker-defined shape, read defensively: `name`, `ok`, `reason`. */
    toolCalls: z.array(z.object({ name: z.string().max(100).optional(), ok: z.boolean().optional(), reason: z.string().max(500).optional() })).max(50),
    spokenAt: IsoInstant.nullable(),
  })
  .meta({ id: 'VoiceTurn' });

const cloudOnly = {
  availability: 'cloud',
  cloudReason: 'Voice ordering runs beside the cloud storefront; its sessions and transcripts are written and kept only in the cloud.',
} as const;

export const ListVoiceConversations = defineQuery({
  ...cloudOnly,
  name: 'online.voiceConversations.list',
  // E-09: legacy's Conversations tab needed Online Store Settings (checked in the browser only).
  backofficeAlso: ['online.settings.manage'],
  summary: "The location's voice ordering sessions, newest first, a page at a time, optionally only abandoned ones or ones with something not understood.",
  input: z.object({
    locationId: LocationId,
    outcome: VoiceOutcome.nullable(),
    hasUnresolved: z.boolean(),
    page: z.int().min(1).max(10000),
    perPage: z.union([z.literal(10), z.literal(25), z.literal(50)]),
  }),
  output: z.object({ conversations: z.array(VoiceConversationSummary), conversationCount: z.int().nonnegative() }),
  permission: 'online.manage',
  scope: 'location',
  errors: [],
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['GET /v1/online-store/voice-conversations'],
});

export const VoiceConversationNotFound = defineError({
  code: 'online.voice_conversation_not_found', httpStatus: 404, message: 'errors.online.voice_conversation_not_found', retryable: false,
  details: z.object({ conversationId: z.string() }),
});

export const GetVoiceConversation = defineQuery({
  ...cloudOnly,
  name: 'online.voiceConversations.get',
  // E-09: legacy's Conversations tab needed Online Store Settings (checked in the browser only).
  backofficeAlso: ['online.settings.manage'],
  summary: 'One voice ordering session with its transcript, read-only.',
  input: z.object({ locationId: LocationId, conversationId: z.uuid({ version: 'v7' }) }),
  output: z.object({ conversation: VoiceConversationSummary.extend({ turns: z.array(VoiceTurn) }) }),
  permission: 'online.manage',
  scope: 'location',
  errors: [VoiceConversationNotFound],
  agent: { expose: false },
  legacy: ['GET /v1/online-store/voice-conversations/{hashId}'],
});

/**
 * QR Table Pay › Settings (SETG-064; legacy QRTableSettings.vue): the For Here / To Go dining mapping, written as the
 * location's five `online.qr.*` settings in one edit, so a failure never leaves half a mapping. Each dining option must
 * be one of the location's own. Radish Direct reads these for a QR table order (B794; Direct plan, contracts).
 */
export const SetQrDiningMapping = defineCommand({
  ...common,
  name: 'online.setQrDiningMapping',
  summary: "Map QR table orders' For Here and To Go to the location's dining options, with the names diners see.",
  input: z.object({
    locationId: LocationId,
    forHere: z.object({ diningOptionId: DiningOptionId.nullable(), label: z.string().max(255) }),
    toGo: z.object({ enabled: z.boolean(), diningOptionId: DiningOptionId.nullable(), label: z.string().max(255) }),
  }),
  output: z.object({ locationId: LocationId }),
  permission: 'qr.manage',
  risk: 'low',
  agent: { expose: false },
  legacy: ['POST /v1/online-store/{id}/dining-templates'],
});

export const onlineCommands = [UpsertOnlineStore, SetBusinessHours, SetAcceptingOrders, SetDefaultPrepTime, SetGatewayPrivateKey, SetQrDiningMapping, ...qrCodeCommands, ...qrTemplateCommands] as const;
export const onlineQueries = [GetOnlineStore, GetOnlineStoreSummary, GetOnlineStoreStatus, ListVoiceConversations, GetVoiceConversation, ...qrCodeQueries, ...qrTemplateQueries] as const;

export * from './orders.ts';
export * from './qr.ts';
export * from './qr-templates.ts';
